import { useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, DollarSign, Plus, Trash2, TrendingUp } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTrip } from '../trips/useTrip'
import { createExpense, deleteExpense, getExpenseSummary, listExpenses } from '../../shared/api/expenses'
import { Button } from '../../shared/components/Button'
import { Spinner } from '../../shared/components/Spinner'
import { EmptyState } from '../../shared/components/EmptyState'
import type { CreateExpenseInput, Expense, ExpenseCategory } from '../../shared/api/types'

const CATEGORIES: { value: ExpenseCategory; label: string }[] = [
  { value: 'food', label: 'Food & drink' },
  { value: 'transport', label: 'Transport' },
  { value: 'lodging', label: 'Lodging' },
  { value: 'activity', label: 'Activity' },
  { value: 'visa', label: 'Visa & fees' },
  { value: 'other', label: 'Other' },
]

const CATEGORY_COLORS: Record<ExpenseCategory, string> = {
  food: 'bg-amber-400',
  transport: 'bg-blue-400',
  lodging: 'bg-purple-400',
  activity: 'bg-green-400',
  visa: 'bg-red-400',
  other: 'bg-gray-400',
}

function categoryLabel(cat: string): string {
  return CATEGORIES.find((c) => c.value === cat)?.label ?? cat
}

function fmt(amount: number, currency: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount)
}

export function Budget() {
  const { id } = useParams<{ id: string }>()
  const tripId = id as string
  const trip = useTrip(id)
  const qc = useQueryClient()

  const expenses = useQuery({
    queryKey: ['expenses', tripId],
    queryFn: () => listExpenses(tripId),
    enabled: !!tripId,
  })

  const summary = useQuery({
    queryKey: ['expenses-summary', tripId],
    queryFn: () => getExpenseSummary(tripId),
    enabled: !!tripId,
  })

  const add = useMutation({
    mutationFn: (input: CreateExpenseInput) => createExpense(tripId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['expenses', tripId] })
      void qc.invalidateQueries({ queryKey: ['expenses-summary', tripId] })
      setForm({ title: '', amount: '', currency: 'USD', category: 'other' })
      setOpen(false)
    },
  })

  const remove = useMutation({
    mutationFn: (expId: string) => deleteExpense(tripId, expId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['expenses', tripId] })
      void qc.invalidateQueries({ queryKey: ['expenses-summary', tripId] })
    },
  })

  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({ title: '', amount: '', currency: 'USD', category: 'other' as ExpenseCategory })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const amount = parseFloat(form.amount)
    if (!form.title.trim() || isNaN(amount) || amount <= 0) return
    add.mutate({ title: form.title.trim(), amount, currency: form.currency, category: form.category })
  }

  const summaryData = summary.data
  const list: Expense[] = expenses.data ?? []

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <Link to={`/trips/${tripId}`} className="text-st-muted hover:text-st-text">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-st-text">Budget</h1>
          {trip.data && (
            <p className="text-sm text-st-muted">{trip.data.title}</p>
          )}
        </div>
      </div>

      {/* Summary card */}
      {summaryData && summaryData.count > 0 && (
        <div className="mb-6 rounded-st-lg border border-st-line bg-st-surface p-5">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-st-muted">
              <TrendingUp className="h-4 w-4" />
              <span className="text-sm font-medium">Total spent</span>
            </div>
            <span className="text-2xl font-bold text-st-text">
              {fmt(summaryData.total, summaryData.currency)}
            </span>
          </div>

          {/* Per-category bars */}
          <div className="space-y-2">
            {Object.entries(summaryData.by_category)
              .sort(([, a], [, b]) => b - a)
              .map(([cat, amount]) => {
                const pct = summaryData.total > 0 ? (amount / summaryData.total) * 100 : 0
                const colorClass = CATEGORY_COLORS[cat as ExpenseCategory] ?? 'bg-gray-400'
                return (
                  <div key={cat}>
                    <div className="mb-1 flex justify-between text-xs text-st-muted">
                      <span>{categoryLabel(cat)}</span>
                      <span>{fmt(amount, summaryData.currency)}</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-st-surface-2">
                      <div
                        className={`h-full rounded-full ${colorClass}`}
                        style={{ width: `${pct.toFixed(1)}%` }}
                      />
                    </div>
                  </div>
                )
              })}
          </div>
        </div>
      )}

      {/* Add expense button / form */}
      {!open ? (
        <Button
          variant="primary"
         
          className="mb-5 flex items-center gap-1.5"
          onClick={() => setOpen(true)}
        >
          <Plus className="h-3.5 w-3.5" />
          Add expense
        </Button>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="mb-5 rounded-st-lg border border-st-line bg-st-surface p-4"
        >
          <p className="mb-3 text-sm font-semibold text-st-text">New expense</p>
          <div className="mb-3 grid gap-3 sm:grid-cols-2">
            <input
              type="text"
              placeholder="Description"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className="col-span-2 rounded-st border border-st-line bg-transparent px-3 py-2 text-sm text-st-text placeholder:text-st-muted focus:outline-none focus:ring-1 focus:ring-st-primary"
              autoFocus
            />
            <input
              type="number"
              min="0.01"
              step="0.01"
              placeholder="Amount"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              className="rounded-st border border-st-line bg-transparent px-3 py-2 text-sm text-st-text placeholder:text-st-muted focus:outline-none focus:ring-1 focus:ring-st-primary"
            />
            <select
              value={form.currency}
              onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
              className="rounded-st border border-st-line bg-st-surface px-3 py-2 text-sm text-st-text focus:outline-none focus:ring-1 focus:ring-st-primary"
            >
              {['USD', 'EUR', 'GBP', 'ILS', 'JPY', 'AUD', 'CAD'].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <select
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as ExpenseCategory }))}
              className="col-span-2 rounded-st border border-st-line bg-st-surface px-3 py-2 text-sm text-st-text focus:outline-none focus:ring-1 focus:ring-st-primary"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={add.isPending}>
              {add.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="ghost"
             
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
          </div>
          {add.isError && (
            <p className="mt-2 text-xs text-red-500">Failed to save expense.</p>
          )}
        </form>
      )}

      {/* Expense list */}
      {expenses.isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : list.length === 0 ? (
        <EmptyState
          Icon={DollarSign}
          title="No expenses yet"
          description="Add expenses to see your budget breakdown."
        />
      ) : (
        <ul className="space-y-2">
          {list.map((exp) => (
            <li
              key={exp.id}
              className="flex items-center justify-between rounded-st-lg border border-st-line bg-st-surface px-4 py-3"
            >
              <div className="flex items-center gap-3">
                <span
                  className={`h-2 w-2 rounded-full ${CATEGORY_COLORS[exp.category] ?? 'bg-gray-400'}`}
                />
                <div>
                  <p className="text-sm font-medium text-st-text">{exp.title}</p>
                  <p className="text-xs text-st-muted">
                    {categoryLabel(exp.category)}
                    {exp.date ? ` · ${exp.date}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-st-text">
                  {fmt(exp.amount, exp.currency)}
                </span>
                <button
                  onClick={() => remove.mutate(exp.id)}
                  disabled={remove.isPending}
                  className="text-st-muted hover:text-red-500 disabled:opacity-40"
                  aria-label="Delete expense"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
